/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Typography, Grid, Button, TextField, Icon, IconButton, Modal, Paper, Box, Collapse } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { get, set, size, last, sortBy, noop, orderBy } from 'global/lib/lodash';
import useStyles from './styles';
import ButtonToggle from 'global/components/btn-toggle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import clsx from 'clsx';
import DropArea from 'global/components/drop-area';
import Camera from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import SnackAlerts from 'global/components/snack-alerts';
import useDebounce from 'global/hooks/useDebounce';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { CommentContainer } from 'feature/report/ChecklistScores';
import { ImageSelectBtn } from 'feature/report/AssessmentScore/SystemScore';
import { scoringClass } from '../../utils';
import { useScreen } from 'global/styles';
import { Button as OButton } from '../Button';
import { Media } from 'global/components/Media';
import CheckPointInfoModal from 'global/components/CheckpointInfo';

const CPLoading = (props) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={5}>
        <Skeleton width={'40%'} height={50} />
        <Skeleton variant="rectangular" height={250} />
      </Grid>
      <Grid item xs={7}>
        <Skeleton variant="rectangular" height={300} />
      </Grid>
    </Grid>
  );
};

export default (props) => {
  const { scoreOnly, isCourse, pssvCheckpoint } = props;
  const isActionable = get(props, 'pssvCheckpoint.filters.act', false);

  const [formState, _setFormState] = useState({});
  const [expandedExtraInfo, setExpandedExtraInfo] = useState(isActionable);
  const [checkpointInfo, setCheckpointInfo] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  // const [loadingImages, setLoadingImages] = useState(false);
  const [changedState, setChangedState] = useState(0);
  const [alerts, setAlerts] = useState();
  const currentRequest = useRef();
  const classes = useStyles({ formState });
  const debouncedForm = useDebounce(changedState, scoreOnly ? 0 : 500);
  const media = useRef({ deleted: [] });
  const [state, setState] = useState({ mediaOpen: false });

  const { isMobileXS } = useScreen();

  const isObs = !pssvCheckpoint.checkpointId;

  const checkpointdata = get(props, 'checkpointdata', {});

  const scos = get(checkpointdata, 'data.scorecardOptions', [])
    .filter((f) => f.scorecardId == get(props, 'pssvCheckpoint.scorecardId') && f.checklistId == get(props, 'pssvCheckpoint.checklistId'))
    .sort((a, b) => a.value - b.value);
  const subTitle = get(checkpointdata, 'data.description');
  const scConfig = get(props, 'scorecard.config', {});
  const useRealScore = get(props, 'useRealScore', false);

  const {
    // allowNA,
    allowNO,
    // defaultDisplay,
    // defaultScore,
    // defaultValue,
    // editScoreInfo,
    // hasSubScores,
    // maxScore,
    // minScore,
    // orderOverrides,
    // scoringClass,
    // step,
    // subScoringClass,
    // weightOverrides,
  } = scConfig;

  // const isSubView = shouldUseSubEditView(scConfig);
  const canEditInfo = get(props, 'canEditInfo', true);
  const isPassFail = size(scos) === 2;

  const toggleCollapse = () => {
    setExpandedExtraInfo(!expandedExtraInfo);
  };

  useEffect(() => {
    setExpandedExtraInfo((isActionable || isObs || isCourse) && (isPassFail || isMobileXS));
  }, [isActionable, isMobileXS, isPassFail]);

  const options = useMemo(() => {
    const ots = scos
      .sort((a, b) => get(a, 'value') - get(b, 'value'))
      .map((sco, i) => ({
        checklistId: pssvCheckpoint?.checklistId,
        supersetId: pssvCheckpoint?.supersetId,
        index: i,
        comment: [subTitle, get(sco, 'criteria')].filter(Boolean).join(' '),
        criteria: get(sco, 'criteria'),
        value: get(sco, 'value'),
      }));

    if (allowNO) {
      const hasConfigLabel = (get(scConfig, 'scoreValueLabel') || []).find((f) => f.value == -1);
      ots.push({
        comment: subTitle,
        label: get(hasConfigLabel, 'label', isPassFail ? 'N/O' : ''),
        value: -1,
        index: -1,
      });
    }

    return ots;
  }, [checkpointdata, scConfig]);

  const scores = useRetrieveData(
    { url: `/scorecard-scores/${get(pssvCheckpoint, 'scorecardScoreId')}` },
    {
      waitFor: get(pssvCheckpoint, 'scorecardScoreId') ? get(pssvCheckpoint, 'scorecardScoreId') : null,
      build: ['scorecardScores'],
    }
  );

  useEffect(() => {
    const scScoreIdIsNull = get(pssvCheckpoint, 'scorecardScoreId') === null;
    const isMinus1 = parseInt(get(pssvCheckpoint, 'value')) === -1;
    if (scScoreIdIsNull && isMinus1) {
      scores.manuallySetData({ ...pssvCheckpoint });
    }
  }, [pssvCheckpoint]);

  const currentScore = last(scores.data);
  const scorecardScoreId = get(pssvCheckpoint, 'scorecardScoreId') || get(currentScore, 'id');

  const loading =
    get(checkpointdata, 'loading', false) ||
    ((!scoreOnly || useRealScore) && pssvCheckpoint && pssvCheckpoint.scorecardScoreId && get(scores, 'loading', false)) ||
    (scoreOnly && !size(scos) && !useRealScore) ||
    (useRealScore && scoreOnly && get(checkpointdata, 'loading'));

  const blocked = (props.queue || []).find((q) => q.id === pssvCheckpoint.checkpointId);

  const hideRightGrid = get(props, 'hideRightGrid', false);

  const setFormState = (state) => {
    _setFormState(state);
  };

  useEffect(() => {
    set(currentRequest, 'current.blocked', props.blocked);
  }, [props.blocked]);

  useEffect(() => {
    if (!blocked && !loading) {
      const scscores = get(scores, 'data', []);
      const currentScore = last(scscores);
      // pssvParentCheckpoint is only used when the checkpoint score is being updated from the SubScoreModal modal.
      const checkpointScoreInfo = get(pssvCheckpoint, 'pssvParentCheckpoint', pssvCheckpoint);
      const checkScore = Math.round(parseFloat(get(checkpointScoreInfo, 'value') || get(checkpointScoreInfo, 'score')));
      // Check for default values
      let ind =
        scoreOnly && !useRealScore
          ? get(props, 'scoreOnlyIndex', 0)
          : isNaN(checkScore) && currentScore
            ? scos.findIndex((f) => parseInt(f.id) === get(currentScore, 'scorecardOptionId'))
            : scos.findIndex((f) => parseInt(f.value) === checkScore);
      const hasImages = get(pssvCheckpoint, 'images');
      const hasCriteria = get(currentScore, 'criteria');
      const hasNotes = isPassFail ? get(currentScore, 'notes') : get(pssvCheckpoint, 'notes');
      const keylength = Object.keys(formState).length;

      if (isPassFail && ind >= 2) {
        ind = 1;
      }

      const scoreOpt = scoreOnly ? ind : checkScore === -1 ? -1 : isNaN(checkScore) ? null : ind;
      set(formState, 'scorecardOption', scoreOpt);
      set(formState, 'initialActive', props.highlightActive ? scos.findIndex((f) => parseInt(f.value) == Math.round(parseFloat(get(props, 'highlightActive')))) : undefined);
      set(formState, 'initialCompare', props.highlightCompare ? scos.findIndex((f) => parseInt(f.value) == Math.round(parseFloat(get(props, 'highlightCompare')))) : undefined);

      if (keylength === 0 || (keylength == 1 && formState.photos)) {
        if (isObs) {
          set(formState, 'comment', get(currentScore, 'notes', pssvCheckpoint.notes));
        } else {
          //only do this on inital load
          if (hasNotes || hasCriteria) {
            set(formState, 'comment', hasCriteria);
            set(formState, 'overwritten', [subTitle, get(scos, `${ind}.criteria`)].filter(Boolean).join(' ') !== hasCriteria);
            set(formState, 'notes', hasNotes);
            setFormState(formState);
          }
        }
        if (hasImages) {
          const imgs = hasImages.map((img, i) => {
            return {
              ...img,
              data: img.thumb,
            };
          });
          set(formState, 'photos', sortBy(imgs, 'orderColumn'));
        }
      }
      _setFormState({ ...formState });

      if (scoreOnly) {
        submit();
      }
    }
  }, [loading, pssvCheckpoint.value]);

  useEffect(() => {
    const photos = (pssvCheckpoint.images || []).map((img) => {
      return {
        ...img,
        ...((formState.photos || []).find((p) => p.id == img.id) || {}),
      };
    });
    _setFormState((fs) => ({ ...fs, photos }));
  }, [pssvCheckpoint.images]);

  // const onError = err => {
  //   setAlerts({
  //     message: err.message,
  //     severity: 'error',
  //   })
  // }

  const buildData = () => {
    const scorecardOption = get(formState, 'scorecardOption');
    const comment = get(formState, 'comment', '');
    const opt = get(options, scorecardOption);

    // const scscores = get(props, 'checkpoint.data.scorecardScores', []).filter(f => (get(f,'assessmentId','') && get(f, 'assessmentId', '').toString() === get(props, 'assessmentId', '').toString()));
    const cpoid = get(opt, 'checkpointOptionId');

    const data = {
      ids: {
        checkpoint: get(props, 'pssvCheckpoint._id'),
        checklist: get(props, 'pssvChecklist._id'),
      },
      deletedMedia: get(media, 'current.deleted', []),
      data: isObs
        ? {
            type: 'scorecard-scores',
            attributes: {
              superset_id: get(props, 'pssvChecklist.supersetId', '').toString(),
              assessment_id: get(props, 'assessmentId', '').toString(),
              scorecard_id: get(props, 'pssvChecklist.scorecardId', '').toString(),
              checklist_id: get(props, 'pssvChecklist.checklistId', '').toString(),
              checkpoint_id: null,
              checkpoint_option_id: null,
              scorecared_option_id: null,
              score: '-1',
              notes: get(formState, 'comment'),
              images: get(formState, 'photos', []).map((p) => ({
                ...p,
                id: p.id || null,
                name: p.path,
                caption: get(p, 'caption', ''),
              })),
            },
          }
        : {
            type: 'scorecard-scores',
            attributes: {
              superset_id: get(props, 'pssvCheckpoint.supersetId', '').toString(),
              assessment_id: get(props, 'assessmentId', '').toString(),
              scorecard_id: get(props, 'pssvCheckpoint.scorecardId', '').toString(),
              checklist_id: get(props, 'pssvCheckpoint.checklistId', '').toString(),
              checkpoint_id: get(props, 'pssvCheckpoint.checkpointId', '').toString(),
              checkpoint_option_id: cpoid ? cpoid.toString() : undefined,
              scorecard_option_id: opt ? get(opt, 'id', '').toString() : undefined,
              score: opt ? get(opt, 'value', '').toString() : scorecardOption,
              notes: get(formState, 'notes'),
              status: props.setTargetStatus,
              images: get(formState, 'photos', []).map((p) => ({
                ...p,
                id: p.id || null,
                name: p.path,
                caption: get(p, 'caption', ''),
              })),
            },
          },
    };

    // Update pssvCheckpoint.score for immediate visual feedback in SubScoreModal
    if (!isObs) {
      set(props.pssvCheckpoint, 'score', opt ? get(opt, 'value', '').toString() : scorecardOption);
    }

    if (!isObs) {
      set(data, 'data.attributes.notes', get(formState, 'notes', ''));
      set(data, 'data.attributes.criteria', comment || '');
    }

    if (scorecardScoreId) {
      set(data, 'data.id', scorecardScoreId.toString());
    }

    if (get(data, 'data.attributes.score') === null) {
      set(data, 'data.attributes', { archived: true, criteria: !isPassFail ? '' : undefined });
    }

    set(media, 'current.deleted', []);
    return data;
  };

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const submit = () => {
    setChangedState(changedState + 1);
  };

  useEffect(() => {
    if (changedState === 0 && !scoreOnly) {
      return;
    }
    props.addToQueue &&
      props.addToQueue({
        id: pssvCheckpoint.checkpointId,
        build: buildData,
      });
  }, [debouncedForm]);

  const renderAddButtons = () => {
    if (!canEditInfo) {
      return null;
    }
    const buttonsEnabled = isObs ? !!get(formState, 'comment') : get(formState, 'scorecardOption', null) !== null;

    const onTextChange = (e) => {
      const val = e.target.value;
      set(formState, 'notes', val);
      setFormState({ ...formState });
      submit();
    };

    const onFilesChange = (files) => {
      set(formState, 'photos', [...files]);
      setFormState({ ...formState });
      submit();
    };

    const deleteImage = (file, index) => {
      const delMedia = get(media, 'current.deleted', []);
      const photos = get(formState, 'photos', []);
      const toRemove = photos[index];
      set(media, 'current.deleted', [...delMedia, toRemove]);
      photos.splice(index, 1);
      set(formState, 'photos', photos);
      setFormState({ ...formState });
      submit();
    };

    const handleTakePhoto = (data) => {
      const photos = get(formState, 'photos', []);
      set(formState, 'photos', [
        ...photos,
        {
          path: `Capture_${new Date().toISOString()}`,
          data,
        },
      ]);
      setFormState({ ...formState });
      setShowCamera(false);
      submit();
    };

    const onImageSubmit = ({ images }) => {
      set(formState, 'photos', [...get(formState, 'photos', []), ...images]);
      setFormState({ ...formState });
      submit();
    };

    const scoInd = get(formState, 'scorecardOption');

    const onOverrideChange = (e) => {
      const val = e.target.value;
      set(formState, 'comment', val);
      set(formState, 'overwritten', val !== [subTitle, get(scos, `${scoInd}.criteria`)].filter(Boolean).join(' ') ? true : false);
      setFormState({ ...formState });
      submit();
    };

    if (showCamera) {
      return (
        <Modal open={true} onClose={() => setShowCamera(false)}>
          <Paper className={classes.cameraContainer}>
            <Camera onTakePhoto={handleTakePhoto} />
            <Button color="primary" variant="contained" className={classes.closeCamera} onClick={() => setShowCamera(false)}>
              <Icon>close</Icon>
            </Button>
          </Paper>
        </Modal>
      );
    }

    const showTextOverride = get(scConfig, 'textOverride', false) && isPassFail;

    const onValuesChange = (o) => {
      setFormState((fs) => {
        let shouldsubmit = false;
        let photos = fs.photos.map((p) => {
          if (p.id == o.id) {
            set(p, 'caption', get(o, 'values.custom_properties.caption'));
            if (o.newimg) {
              p = { ...o.newimg, ...get(o, 'newimg.attributes', {}) };
              shouldsubmit = true;
            }
          }
          return p;
        });

        if (o.archived) {
          photos = fs.photos.filter((p) => p.id != o.id);
          shouldsubmit = true;
        }

        if (o.newimg) {
          setState((s) => ({ ...s, mediaOpen: false }));
        }

        set(formState, 'photos', photos);

        if (shouldsubmit) {
          submit();
        }

        return {
          ...fs,
          photos,
        };
      });
    };

    return (
      <div className={isPassFail ? undefined : classes.commentBtnContainer}>
        {!!showTextOverride && (
          <div style={{ marginBottom: 24 }}>
            <TextField className={classes.comment} onChange={onOverrideChange} helperText={'Text Override'} value={get(formState, 'comment', '')} multiline rows={6} />
          </div>
        )}

        <div className={classes.commentBtn}>
          <DropArea
            accept={['image/*', 'video/*']}
            onFilesChange={onFilesChange}
            onSort={onFilesChange}
            files={[...get(formState, 'photos', [])]}
            sortable={true}
            disabled={!buttonsEnabled}
            renderContent={() => <>Drop/Click to Upload Files</>}
            renderFile={(file, key) => {
              let i = get(formState, `photos`, []).findIndex((f) => f.id == file.id);
              if (i < 0) {
                i = key;
              }
              const loc = `photos.${i}.caption`;

              return (
                <div key={`${file.id}_${key}_${i}`} className={classes.imageWrap} onClick={stopProp}>
                  <Media
                    {...file}
                    canEdit={!key.includes('_file_')}
                    onValuesChange={onValuesChange}
                    onClose={(s) => {
                      if (s.hasEdits && props.setNeedUpdate) {
                        props.setNeedUpdate();
                      }
                      setState((s) => ({ ...s, mediaOpen: false }));
                    }}
                    notAllowed={['tag']}
                    images={get(formState, 'photos')}
                    index={i}
                    assessment={props.assessment}
                    imagetype="scorecard-scores"
                    parentId={scorecardScoreId}
                    childImagesIncludedInEdit
                    uploadAllNew
                    onOpen={() => setState((s) => ({ ...s, mediaOpen: true }))}
                  />
                  {!file.file && !state.mediaOpen && (
                    <TextField
                      placeholder="caption"
                      defaultValue={get(formState, loc) || ''}
                      size="small"
                      onChange={(e) => {
                        set(formState, loc, e.target.value);
                        setFormState({ ...formState });
                        submit();
                      }}
                      className={classes.comment}
                    />
                  )}
                  <IconButton className={classes.imageDelete} onClick={() => deleteImage(file, i)} size="large">
                    <Icon>close</Icon>
                  </IconButton>
                </div>
              );
            }}
          />

          {!props.isCourse && (
            <div className={classes.imageSelectBtn}>
              <ImageSelectBtn disabled={!buttonsEnabled} onSubmit={onImageSubmit} superset_id={get(props, 'pssvChecklist.supersetId')} assessment={get(props, 'assessment.data')} />
            </div>
          )}
        </div>

        {!isObs && (
          <div className={classes.commentBtn}>
            <TextField
              className={clsx(classes.comment, classes.commentWithClose)}
              onChange={onTextChange}
              helperText="Notes"
              placeholder="Additional notes"
              value={get(formState, 'notes', '')}
              multiline
              rows={5}
              disabled={!buttonsEnabled}
            />
          </div>
        )}
      </div>
    );
  };

  const handleOpenCheckpointInfo = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCheckpointInfo({
      open: true,
      onClose: () => setCheckpointInfo({}),
      checkpoint: { id: get(checkpointdata, 'data.id'), ...get(checkpointdata, 'data') },
      checklist: get(props, 'pssvChecklist'),
      scorecard: get(props, 'scorecard'),
    });
  };

  const renderLeftGrid = () => {
    const hasImage = get(props, 'pssvChecklist.checklist.image');

    const onSelectOption = (opt, index) => {
      const currentOption = get(formState, 'scorecardOption');
      const nullify = currentOption === index && !get(pssvCheckpoint, 'templateUnchanged', false) ? true : false;

      if (nullify && !scorecardScoreId) {
        return;
      }

      set(formState, 'scorecardOption', nullify ? null : index);
      const currentComment = get(formState, 'comment');
      const overwritten = get(formState, 'overwritten');
      const alwaysWrite = get(props, 'alwaysSetComment', false);
      if (!overwritten || !currentComment || alwaysWrite) {
        (opt.comment || alwaysWrite) && set(formState, 'comment', opt.comment);
      }
      if (nullify) {
        set(formState, 'comment', '');
        set(formState, 'overwritten', false);
        // setShowPhoto(false);
      }
      setFormState({ ...formState });

      submit();
    };

    const comments = get(
      get(props, 'assessment.data.scorecardScores', []).find((c) => c.id == get(props, 'pssvCheckpoint.scorecardScoreId')),
      'comments',
      []
    );

    const renderText = () => {
      if (scoreOnly) {
        return null;
      }

      return (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" className={clsx(classes.checkpointHeader, classes.checkpointHeaderText)}>
            <Typography variant="h5">{get(checkpointdata, 'data.name')}</Typography>
            {props.hideInfoIcon ? null : (
              <IconButton color="primary" onClick={handleOpenCheckpointInfo} size="large">
                <InfoOutlinedIcon />
              </IconButton>
            )}
          </Box>
          <Typography variant="subtitle1">{subTitle}</Typography>
          {!!size(comments) && comments.map((com) => <CommentContainer comment={com} isCheckpointEditMode />)}
        </>
      );
    };

    const btnDisabled = get(props, 'pssvCheckpoint.value') === null;

    return isObs ? null : (
      <Grid item xs={12} md={isPassFail || hideRightGrid ? (hasImage || hideRightGrid ? 12 : 7) : 5}>
        {renderText()}
        {props.renderObservations && props.renderObservations()}
        <ButtonToggle
          variant={isPassFail ? 'passFail' : undefined}
          options={options}
          isCompare={props.highlightCompare ? { value: get(formState, 'initialCompare'), text: get(props, 'setTargetStatus') === 'goal' ? 'target' : get(props, 'setTargetStatus') } : undefined}
          isActive={props.highlightActive ? { value: get(formState, 'initialActive') } : undefined}
          selected={get(formState, 'scorecardOption')}
          onSelect={onSelectOption}
          scorecardConfig={scConfig}
          isDisabled={!get(props, 'canScore', true)}
          templateUnchanged={!!get(pssvCheckpoint, 'templateUnchanged', false)}
        />
        {(isPassFail || isMobileXS) && !scoreOnly && canEditInfo && !hideRightGrid && (
          <>
            <OButton
              style={{ visibility: isCourse || isActionable || isObs ? 'hidden' : undefined }}
              className={classes.expandBtn}
              variant="default"
              endIcon={<Icon>{expandedExtraInfo ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>}
              disabled={btnDisabled}
              onClick={toggleCollapse}
              title={expandedExtraInfo ? 'Hide' : 'Show'}
            />
            {isPassFail && <Collapse in={expandedExtraInfo}>{renderAddButtons()}</Collapse>}
          </>
        )}
      </Grid>
    );
  };

  const renderRightGrid = () => {
    if (isPassFail || hideRightGrid || scoreOnly || !canEditInfo) {
      return null;
    }

    const scoInd = get(formState, 'scorecardOption');

    const onTextChange = (e) => {
      const val = e.target.value;
      set(formState, 'comment', val);
      set(formState, 'overwritten', val !== [subTitle, get(scos, `${scoInd}.criteria`)].filter(Boolean).join(' ') ? true : false);
      setFormState({ ...formState });
      submit();
    };

    const inputDisabled = isObs ? false : scoInd === null || scoInd === undefined;
    const textVisible = isObs ? true : get(scConfig, 'textOverride', false);

    return (
      <Grid item xs={12} md={isObs ? 12 : 7}>
        <div style={{ marginTop: isObs || isMobileXS ? 0 : 33 }}>
          {/* Always expanded when on large screen, but variable when on a small screen */}
          <Collapse in={!isMobileXS || expandedExtraInfo || isObs}>
            {!!textVisible && (
              <TextField
                disabled={inputDisabled}
                className={isObs ? undefined : classes.comment}
                onChange={onTextChange}
                helperText={isObs ? undefined : 'Text Override'}
                value={get(formState, 'comment', '')}
                multiline
                label={isObs ? 'General Observations' : undefined}
                placeholder={isObs ? 'General Observations' : undefined}
                rows={isObs ? 5 : 6}
                fullWidth={isObs}
                variant={isObs ? 'outlined' : undefined}
              />
            )}
            {renderAddButtons()}
          </Collapse>
        </div>
      </Grid>
    );
  };

  if (get(props, 'loading', false)) return <CPLoading />;

  return (
    <>
      <Grid container spacing={2}>
        {renderLeftGrid()}

        {renderRightGrid()}
        <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
        {blocked && <div className={classes.blockOverlay} />}
      </Grid>

      {checkpointInfo?.open ? <CheckPointInfoModal {...checkpointInfo} /> : null}
    </>
  );
};
